import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageSquare, ChevronDown, ChevronUp, Filter, Trash2 } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_LABELS = {
  too_expensive: 'Too expensive / Pricing',
  not_using: 'Not using enough / User experience',
  found_alternative: 'Found alternative',
  missing_features: 'Missing features',
  technical_issues: 'Technical issues',
  other: 'Other'
};

export default function CancellationFeedback({ user, onLogout }) {
  const [allFeedback, setAllFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedCards, setExpandedCards] = useState({});
  const [filterReason, setFilterReason] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/coach/all-feedback`);
      setAllFeedback(response.data.feedback);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const toggleCard = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleDeleteFeedback = async (feedbackId, e) => {
    e.stopPropagation(); // Prevent card expansion when clicking delete
    
    if (!confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }
    
    setDeletingId(feedbackId);
    try {
      await axios.delete(`${API}/coach/feedback/${feedbackId}`);
      toast.success('Feedback deleted');
      
      // Remove from local state
      setAllFeedback(prev => prev.filter(f => f.id !== feedbackId));
      
      // Refresh to update stats
      await fetchFeedback();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete feedback');
    } finally {
      setDeletingId(null);
    }
  };

  // Filter feedback based on active tab
  const getFilteredFeedback = () => {
    let filtered = allFeedback;

    // Tab filtering
    if (activeTab === 'general') {
      // General feedback: users who submitted feedback but didn't cancel OR who haven't cancelled
      filtered = filtered.filter(f => f.is_general_feedback || !f.cancelled_at);
    } else if (activeTab === 'cancellation') {
      // Cancellation feedback: users who cancelled
      filtered = filtered.filter(f => f.cancelled_at && !f.is_general_feedback);
    }

    // Reason filtering
    if (filterReason !== 'all') {
      filtered = filtered.filter(f => 
        f.reasons && f.reasons.includes(filterReason)
      );
    }

    return filtered;
  };

  const feedback = getFilteredFeedback();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Client Feedback</h1>
          <p className="text-gray-600 mt-2">All feedback from clients - general and cancellation</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All Feedback
            {stats && <span className="ml-2 text-sm">({stats.total_all})</span>}
          </button>
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'general'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            General
            {stats && <span className="ml-2 text-sm">({stats.total_general})</span>}
          </button>
          <button
            onClick={() => setActiveTab('cancellation')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'cancellation'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Cancellations
            {stats && <span className="ml-2 text-sm">({stats.total_cancellation})</span>}
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Total Feedback Received</CardDescription>
                <CardTitle className="text-3xl">{stats.with_feedback}</CardTitle>
              </CardHeader>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Cancellations</CardDescription>
                <CardTitle className="text-3xl flex items-center gap-2">
                  {stats.total_cancellation}
                  <span className="text-sm text-gray-500 font-normal">
                    ({stats.cancellation_with_feedback} with feedback)
                  </span>
                </CardTitle>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Top Reason</CardDescription>
                <CardTitle className="text-lg">
                  {stats.top_reason ? REASON_LABELS[stats.top_reason] : 'N/A'}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Reason Breakdown & Filter */}
        {stats && stats.reason_breakdown && Object.keys(stats.reason_breakdown).length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Feedback Reasons</CardTitle>
                  <CardDescription>Click to filter by reason</CardDescription>
                </div>
                {filterReason !== 'all' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterReason('all')}
                    className="text-xs"
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats.reason_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => (
                    <button
                      key={reason}
                      onClick={() => setFilterReason(reason)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                        filterReason === reason
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'hover:bg-gray-50 border-2 border-transparent'
                      }`}
                    >
                      <span className={`text-sm font-medium ${
                        filterReason === reason ? 'text-blue-700' : 'text-gray-700'
                      }`}>
                        {REASON_LABELS[reason] || reason}
                      </span>
                      <Badge variant={filterReason === reason ? "default" : "secondary"}>
                        {count}
                      </Badge>
                    </button>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {activeTab === 'all' && 'All Feedback'}
                  {activeTab === 'general' && 'General Feedback'}
                  {activeTab === 'cancellation' && 'Cancellation Feedback'}
                </CardTitle>
                <CardDescription>
                  {feedback.length} {feedback.length === 1 ? 'item' : 'items'}
                  {filterReason !== 'all' && ` filtered by "${REASON_LABELS[filterReason]}"`}
                </CardDescription>
              </div>
              {filterReason !== 'all' && (
                <Filter className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-gray-500 text-center py-8">Loading feedback...</p>
            ) : feedback.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No feedback {filterReason !== 'all' ? 'matching this filter' : 'yet'}
              </p>
            ) : (
              <div className="space-y-3">
                {feedback.map((item) => {
                  const isExpanded = expandedCards[item.id];
                  const hasContent = (item.reasons && item.reasons.length > 0) || item.additional_feedback;
                  
                  return (
                    <div 
                      key={item.id} 
                      className={`border rounded-lg transition-all ${
                        hasContent ? 'hover:shadow-md cursor-pointer' : ''
                      } ${isExpanded ? 'shadow-md' : ''}`}
                      onClick={() => hasContent && toggleCard(item.id)}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <p className="font-semibold text-gray-900">{item.user_name}</p>
                              {item.cancelled_at && (
                                <Badge variant="destructive" className="text-xs">
                                  Cancelled
                                </Badge>
                              )}
                              {!item.cancelled_at && item.reasons && item.reasons.length > 0 && (
                                <Badge variant="default" className="text-xs bg-green-600">
                                  General
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{item.user_email}</p>
                            {!isExpanded && (
                              <div className="mt-2 text-xs text-gray-500">
                                {item.subscription_plan && `Plan: ${item.subscription_plan}`}
                                {item.cancelled_at && ` • Cancelled: ${formatDate(item.cancelled_at)}`}
                                {item.feedback_submitted_at && ` • Submitted: ${formatDate(item.feedback_submitted_at)}`}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Delete button - always show for coaches */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteFeedback(item.id, e)}
                              disabled={deletingId === item.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              title="Delete this record"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {!hasContent && (
                              <Badge variant="outline" className="text-xs text-gray-400">
                                No feedback yet
                              </Badge>
                            )}
                            {hasContent && (
                              <div className="text-gray-400">
                                {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && hasContent && (
                          <div className="mt-4 pt-4 border-t space-y-3 animate-in fade-in duration-200">
                            {item.reasons && item.reasons.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">REASONS SELECTED</p>
                                <div className="flex flex-wrap gap-2">
                                  {item.reasons.map((reason) => (
                                    <Badge key={reason} variant="outline" className="text-xs">
                                      {REASON_LABELS[reason] || reason}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {item.additional_feedback && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">ADDITIONAL COMMENTS</p>
                                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                  <MessageSquare className="w-4 h-4 inline mr-2 text-blue-600" />
                                  <span className="text-sm text-gray-800">
                                    "{item.additional_feedback}"
                                  </span>
                                </div>
                              </div>
                            )}

                            <div className="pt-2 border-t">
                              <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                                <div>
                                  <span className="font-medium">Plan:</span> {item.subscription_plan || 'N/A'}
                                </div>
                                <div>
                                  <span className="font-medium">Duration:</span> {item.subscription_duration_days || 'N/A'} days
                                </div>
                                {item.cancelled_at && (
                                  <div>
                                    <span className="font-medium">Cancelled:</span> {formatDate(item.cancelled_at)}
                                  </div>
                                )}
                                {item.feedback_submitted_at && (
                                  <div>
                                    <span className="font-medium">Feedback Submitted:</span> {formatDate(item.feedback_submitted_at)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
